import type { VersionNode } from '@/lib/contract';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/cn';
import { MicroLabel } from '@/ui/micro-label';
import { formatDay } from '@/lib/format';

const STATE_LABEL: Record<VersionNode['state'], string> = {
  raw_intake: 'Raw intake',
  remediated: 'Remediated',
  certified: 'Certified'
};

/** v1.0 -> v1.1 -> v2.0. The certified node is accented; earlier nodes are historical. */
export function VersionLineage({ nodes, className }: { nodes: VersionNode[]; className?: string }) {
  return (
    <div className={cn('flex flex-wrap items-stretch gap-3', className)}>
      {nodes.map((node, index) => {
        const certified = node.state === 'certified';
        return (
          <div key={node.version_label} className="flex flex-1 basis-0 items-center gap-3">
            <div
              className={cn(
                'flex-1 rounded border px-4 py-3',
                certified ? 'border-primary bg-card' : 'border-border bg-card'
              )}
            >
              <div className="flex items-baseline gap-2">
                <span className="font-mono text-[13px] text-foreground">{node.version_label}</span>
                <MicroLabel size="xs" tone={certified ? 'primary' : 'dim'}>
                  {STATE_LABEL[node.state]}
                </MicroLabel>
              </div>
              {/* Rendered raw until now, which is why this panel showed `2026-08-29T23:40:41.451Z`. */}
              <MicroLabel size="xs" className="mt-2 block">
                {formatDay(node.occurred_at)}
              </MicroLabel>
              <p className="mt-2 font-mono text-[10px] leading-relaxed tracking-[0.06em] whitespace-pre-line text-muted">
                {node.note}
              </p>
            </div>
            <ChevronRight className={cn('size-4 shrink-0 text-primary', index === nodes.length - 1 && 'invisible')} />
          </div>
        );
      })}
    </div>
  );
}
